import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { CustomInput } from "../../components/custom/custom-input.jsx";
import { CustomTextarea } from "../../components/custom/custom-textarea.jsx";
import { CustomSelect } from "../../components/custom/custom-select.jsx";
import { CustomButton } from "../../components/custom/custom-button.jsx";
import { selectStyles } from "../../utils/select.js";
import { useAppContext } from "../../context/AppContext.js";
import { useQueryClient } from "@tanstack/react-query";
import paymentFrequencies from "../../json/paymentFrequency.json";
import EmailInput from "../Email/EmailInput.js";
import axiosInstance from "../../services/api.service.js";
import LightLoader from "../Loader/LightLoader/LightLoader.jsx";
import helperService from "../../services/helper.service.js";

function PayoutNotification() {
	const queryClient = useQueryClient();
	const { showToastSuccess, showToastError } = useAppContext();
	const [emails, setEmails] = useState([]);
	const [loading, setLoading] = useState(false);

	const {
		register,
		formState: { errors },
		handleSubmit,
		setValue,
		watch,
		control,
		reset,
	} = useForm();

	const watchAmount = watch("amount");

	function formatWithCommas(value) {
		const number = value.replace(/,/g, "").replace(/\D/g, "");
		return number.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
	}

	useEffect(() => {
		if (watchAmount) {
			const raw = watchAmount.replace(/,/g, "").replace(/\D/g, "");
			if (!isNaN(raw)) {
				const formatted = formatWithCommas(raw);
				if (formatted !== watchAmount) {
					setValue("amount", formatted);
				}
			}
		}
	}, [watchAmount, setValue]);

	const submit = async (data) => {
		const payload = {
			...data,
			amount: +helperService.cleanAmounts(data.amount),
			notifyTeams: emails,
			cycle: +data.cycle,
		};

		setLoading(true);
		try {
			const response = await axiosInstance.post("/api/v1/payout", payload);
			showToastSuccess(
				response?.data?.message || "Payout notification sent successfully"
			);

			reset({
				clientName: "",
				amount: "",
				frequency: null,
				dueDate: "",
				cycle: "",
				additionalNotes: "",
			});
			setEmails([]);
		} catch (error) {
			showToastError(error?.response?.data?.message || "An error occurred");
		} finally {
			queryClient.invalidateQueries(["all-payouts"]);
			setLoading(false);
		}
	};

	return (
		<div className="flex flex-col gap-6 mb-5">
			<h1 className="text-[16px] font-[600] text-[#1E1E1E] font-jakarta">
				Create Payout Notification
			</h1>

			<form className="flex flex-col gap-2" onSubmit={handleSubmit(submit)}>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full md:w-[90%]">
					<div className="flex flex-col gap-2">
						<div className="col-span-1">
							<CustomInput
								label="Client Name"
								{...register("clientName", {
									required: "Please enter Client Name",
								})}
								required={true}
								placeholder="Enter client name"
								acceptNumbers={false}
								error={errors.clientName}
								errorText={errors.clientName && errors.clientName.message}
							/>
						</div>
						<div className="relative col-span-1">
							<p className="absolute z-[2] top-[35px] left-3">₦</p>
							<CustomInput
								type="text"
								inputMode="numeric"
								label="Payout Amount"
								{...register("amount", {
									required: "Please enter payout amount",
									validate: (val) => parseInt(val.replace(/,/g, "")) > 0,
								})}
								required={true}
								placeholder="Enter amount"
								acceptAlphabets={false}
								error={errors.amount}
								errorText={errors.amount && errors.amount.message}
								className="pl-7"
							/>
							{/* {watchAmount ? (
								<span className="absolute top-[30px] right-[8px] bg-[#d3cccc] text-[#1F2937] text-[13px] py-1 px-2 rounded-[5px]">
									₦ {helperService.formatNumber(watchAmount, 2)}
								</span>
							) : null} */}
						</div>
						<div className="col-span-1">
							<CustomSelect
								control={control}
								name="frequency"
								label="Payment Frequency"
								options={paymentFrequencies}
								placeholder="Select frequency"
								getOptionValue={(option) => option.value}
								getOptionLabel={(option) => option.label}
								rules={{
									required: "Please select payment frequency",
								}}
								error={errors.frequency}
								styles={selectStyles("#C9C9C9", "42px", "#ffffff")}
								errorText={errors.frequency && errors.frequency.message}
								isRequired={true}
							/>
						</div>
						<div className="flex gap-5">
							<CustomInput
								label="Start Date"
								{...register("dueDate", {
									required: "Please enter a start date",
								})}
								type="date"
								required={true}
								placeholder="Enter start date"
								acceptNumbers={false}
								error={errors.dueDate}
								errorText={errors.dueDate && errors.dueDate.message}
							/>
							<CustomInput
								label="Number of Cycles"
								{...register("cycle", {
									// required: "Please enter number of cycles",
								})}
								type="number"
								min={1}
								placeholder="Enter number of cycles"
								acceptAlphabets={false}
								hasHint={true}
								hint="This is the number of times the payout will be made. (For example, if you want to pay 3 times, enter 3)"
								// required={true}
								// error={errors.cycle}
								// errorText={errors.cycle && errors.cycle.message}
							/>
						</div>
					</div>
					<div className="flex flex-col gap-2">
						<div className="col-span-1">
							<p className="text-[#1E1E1E] text-[13px] font-[400] mb-1">
								Notify Teams <span className="text-[#FF3B30]">*</span>
							</p>
							<EmailInput
								emails={emails}
								setEmails={setEmails}
								style="default"
								placeholder="Enter email"
							/>
						</div>
						<div className="col-span-1">
							<CustomTextarea
								label="Additional Notes"
								{...register("additionalNotes")}
								placeholder="type here..."
								height="h-[120px]"
							/>
						</div>
					</div>
				</div>
				<div className="flex justify-end w-full md:w-[90%]">
					<CustomButton
						type="submit"
						className="!bg-[#002564] !border-none !outline-none !w-full md:!w-[180px] !h-[40px]"
					>
						{loading ? <LightLoader /> : "Send Notification"}
					</CustomButton>
				</div>
			</form>
		</div>
	);
}

export default PayoutNotification;
